import { useId, useState } from 'react';
import type * as React from 'react';
import { useCreate, useCustomMutation, useDelete, useUpdate } from '@refinedev/core';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { DataTable, type Column } from '@/components/data-table';
import { EmptyState } from '@/components/states';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogForm,
  DialogBody,
  DialogContent,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dialog';
import { FormField, Input, NativeSelect } from '@/components/ui/field';
import { Badge, Mono } from '@/components/ui/primitives';
import { RESOURCES, type AdminRoleValue, type AdminUser } from '@/api/resources';
import { useAdminList } from '@/hooks/use-admin-resource';
import { ALL_ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/auth/rbac';
import { useSession } from '@/auth/use-session';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';
import { formatDateTime, formatRelative } from '@/lib/format';

/**
 * Admin user management (06 §2, 03 §12.1) — `SUPER_ADMIN` only.
 *
 * ⚠️ **The last active `SUPER_ADMIN` cannot be deleted, disabled or demoted,
 * and nobody may demote or disable themselves.** The actions are hidden when
 * either applies, but the server is what enforces it and answers
 * `422 LAST_SUPER_ADMIN` — one careless role change would otherwise lock the
 * whole team out permanently, and a hidden menu item is not a lock.
 *
 * ⚠️ Every call on this screen writes an `AuditLog` row, the reads included.
 * Loading this page is itself an audited event.
 */
export function AdminsPage(): React.JSX.Element {
  const { rows, isLoading, error, refetch, invalidate } = useAdminList<AdminUser>(RESOURCES.admins);
  const session = useSession();

  const [inviting, setInviting] = useState(false);
  const [editingRole, setEditingRole] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);

  const { mutateAsync: remove } = useDelete();
  const { mutateAsync: act } = useCustomMutation();

  const activeSuperAdmins = (rows ?? []).filter(
    (admin) => admin.role === 'SUPER_ADMIN' && admin.status === 'ACTIVE',
  );

  const isLastSuperAdmin = (admin: AdminUser): boolean =>
    admin.role === 'SUPER_ADMIN' && admin.status === 'ACTIVE' && activeSuperAdmins.length <= 1;

  const isSelf = (admin: AdminUser): boolean => admin.id === session?.adminId;

  const runAction = async (
    admin: AdminUser,
    action: 'disable' | 'enable' | 'reset-mfa' | 'resend-invite',
    successMessage: string,
  ): Promise<void> => {
    try {
      await act({
        url: `${RESOURCES.admins}/${admin.id}/${action}`,
        method: 'post',
        values: {},
        successNotification: false,
        errorNotification: false,
      });
      notify.success(successMessage, admin.email);
    } catch (err) {
      notify.error(err, 'That action did not complete');
    } finally {
      invalidate();
    }
  };

  const columns: Array<Column<AdminUser>> = [
    {
      key: 'name',
      header: 'Name',
      sortValue: (row) => `${row.lastName} ${row.firstName}`,
      render: (row) => (
        <span>
          <span className="block font-medium text-ink">
            {row.firstName} {row.lastName}
            {isSelf(row) ? <span className="ml-2 text-xs text-ink-faint">You</span> : null}
          </span>
          <span className="block text-xs text-ink-soft">{row.email}</span>
        </span>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortValue: (row) => row.role,
      render: (row) => <Badge tone="accent">{ROLE_LABELS[row.role]}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (row) => row.status,
      render: (row) =>
        row.status === 'ACTIVE' ? (
          <Badge tone="success">Active</Badge>
        ) : row.status === 'INVITED' ? (
          <Badge tone="caution">Invited</Badge>
        ) : (
          <Badge tone="neutral">Disabled</Badge>
        ),
    },
    {
      key: 'mfa',
      header: 'MFA',
      hideBelow: 'md',
      render: (row) =>
        row.mfaEnabledAt ? (
          <Badge tone="success">Enrolled</Badge>
        ) : (
          <Badge tone="danger">Not enrolled</Badge>
        ),
    },
    {
      key: 'lastLoginAt',
      header: 'Last sign-in',
      hideBelow: 'lg',
      sortValue: (row) => row.lastLoginAt ?? '',
      render: (row) =>
        row.lastLoginAt ? (
          <time dateTime={row.lastLoginAt} title={formatDateTime(row.lastLoginAt)}>
            {formatRelative(row.lastLoginAt)}
          </time>
        ) : (
          <span className="text-xs text-ink-faint">Never</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '3.5rem',
      render: (row) => {
        const locked = isLastSuperAdmin(row);
        const self = isSelf(row);

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Actions for ${row.firstName} ${row.lastName}`}
              >
                <MoreHorizontal aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>{row.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem disabled={locked || self} onSelect={() => setEditingRole(row)}>
                Change role
              </DropdownMenuItem>

              {row.status === 'DISABLED' ? (
                <DropdownMenuItem onSelect={() => void runAction(row, 'enable', 'Enabled')}>
                  Enable
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={locked || self}
                  onSelect={() => void runAction(row, 'disable', 'Disabled')}
                >
                  Disable
                </DropdownMenuItem>
              )}

              <DropdownMenuItem onSelect={() => void runAction(row, 'reset-mfa', 'MFA reset')}>
                Reset MFA
              </DropdownMenuItem>

              {row.status === 'INVITED' ? (
                <DropdownMenuItem
                  onSelect={() => void runAction(row, 'resend-invite', 'Invitation resent')}
                >
                  Resend invitation
                </DropdownMenuItem>
              ) : null}

              <DropdownMenuSeparator />
              <DropdownMenuItem destructive disabled={locked} onSelect={() => setDeleting(row)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <Page
      title="Admin users"
      description="Who can sign in to this panel, and what they can reach."
      breadcrumbs={[{ label: 'Administration' }, { label: 'Admin users' }]}
      width="wide"
      action={
        <Button variant="primary" onClick={() => setInviting(true)}>
          <Plus aria-hidden="true" />
          Invite admin
        </Button>
      }
    >
      <div className="space-y-5">
        <Notice title="The last super admin is protected">
          Deleting, disabling or demoting the only active super admin is refused, and nobody can
          demote or disable themselves. Promote a second super admin before changing this one.
        </Notice>

        <DataTable
          label="Admin users"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          endpoint="GET /admin/admins"
          empty={
            <EmptyState
              title="No admin accounts"
              description="Invite the first admin. They set their own password and enrol MFA from the emailed link."
              action={
                <Button variant="primary" onClick={() => setInviting(true)}>
                  <Plus aria-hidden="true" />
                  Invite admin
                </Button>
              }
            />
          }
          footer={<span>Reads on this screen are recorded in the audit log.</span>}
        />
      </div>

      {inviting ? (
        <InviteDialog
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            invalidate();
          }}
        />
      ) : null}

      {editingRole ? (
        <RoleDialog
          admin={editingRole}
          onClose={() => setEditingRole(null)}
          onSaved={() => {
            setEditingRole(null);
            invalidate();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title="Delete this admin?"
        description={
          <>
            {deleting?.firstName} {deleting?.lastName} loses access immediately and their sessions
            are revoked. This cannot be undone.
          </>
        }
        confirmLabel="Delete admin"
        destructive
        typedConfirmation={deleting?.email}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove({
              resource: RESOURCES.admins,
              id: deleting.id,
              successNotification: false,
              errorNotification: false,
            });
            notify.success('Deleted', deleting.email);
            setDeleting(null);
          } catch (err) {
            notify.error(err, 'The admin was not deleted');
          } finally {
            invalidate();
          }
        }}
      />
    </Page>
  );
}

// ---------------------------------------------------------------------------

function InviteDialog({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: () => void;
}): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<AdminRoleValue>('SUPPORT');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  const emailId = useId();
  const firstId = useId();
  const lastId = useId();
  const roleId = useId();

  const { mutateAsync: create } = useCreate();

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setFieldErrors({});
    try {
      await create({
        resource: RESOURCES.admins,
        values: {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role,
        },
        successNotification: false,
        errorNotification: false,
      });
      notify.success('Invitation sent', `${email.trim()} has 48 hours to accept.`);
      onInvited();
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      notify.error(err, 'The invitation was not sent');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        title="Invite an admin"
        description="They set their own password and enrol MFA from the emailed link. You never see it."
      >
        <DialogForm
          onSubmit={(event) => {
            void submit(event);
          }}
          noValidate
        >
          <DialogBody>
            <FormField label="Email" htmlFor={emailId} error={fieldErrors['email']} required>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="off"
                autoFocus
                required
              />
            </FormField>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="First name"
                htmlFor={firstId}
                error={fieldErrors['firstName']}
                required
              >
                <Input
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  maxLength={50}
                  required
                />
              </FormField>

              <FormField
                label="Last name"
                htmlFor={lastId}
                error={fieldErrors['lastName']}
                required
              >
                <Input
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  maxLength={50}
                  required
                />
              </FormField>
            </div>

            <FormField
              label="Role"
              htmlFor={roleId}
              hint={ROLE_DESCRIPTIONS[role]}
              error={fieldErrors['role']}
              required
            >
              <NativeSelect
                value={role}
                onChange={(event) => setRole(event.target.value as AdminRoleValue)}
              >
                {ALL_ROLES.map((value) => (
                  <option key={value} value={value}>
                    {ROLE_LABELS[value]}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={pending}
              disabled={!email.trim() || !firstName.trim() || !lastName.trim()}
            >
              Send invitation
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function RoleDialog({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [role, setRole] = useState<AdminRoleValue>(admin.role);
  const [pending, setPending] = useState(false);
  const roleId = useId();
  const { mutateAsync: update } = useUpdate();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        title={`Change role for ${admin.firstName} ${admin.lastName}`}
        description="Takes effect on their next request."
      >
        <DialogForm
          onSubmit={(event) => {
            event.preventDefault();
            setPending(true);
            void (async () => {
              try {
                await update({
                  resource: RESOURCES.admins,
                  id: admin.id,
                  values: { role },
                  successNotification: false,
                  errorNotification: false,
                });
                notify.success('Role changed', `${admin.email} is now ${ROLE_LABELS[role]}.`);
                onSaved();
              } catch (err) {
                notify.error(err, 'The role was not changed');
              } finally {
                setPending(false);
              }
            })();
          }}
        >
          <DialogBody>
            <FormField label="Role" htmlFor={roleId} hint={ROLE_DESCRIPTIONS[role]} required>
              <NativeSelect
                value={role}
                onChange={(event) => setRole(event.target.value as AdminRoleValue)}
              >
                {ALL_ROLES.map((value) => (
                  <option key={value} value={value}>
                    {ROLE_LABELS[value]}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
            <p className="text-xs text-ink-faint">
              Admin id <Mono>{admin.id}</Mono>
            </p>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={pending}
              disabled={role === admin.role}
            >
              Change role
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
